/**
 * React Query hooks for fetching large UPS configurations
 * @see GitLab #996 — Repeating Sections (Phase 2: Cooling)
 */

import { useQuery } from '@tanstack/react-query';

import { isRepeatingCollectionRouteNotFound } from './repeatingCollectionRouteNotFound';
import { useNetworkStatus } from './useOfflineRequestsQuery';
import { groupLargeUpsByStatus } from '../../components/largeups/largeUpsListUtils';
import { api } from '../../services/api';
import {
  applyPendingLargeUpsOperations,
  getCachedLargeUpsList,
  getOfflineLargeUpsById,
  getOfflineLargeUpsGrouped,
  getOfflineLargeUpsList,
  getInFlightLargeUpsOpsForRequest,
  mirrorLargeUpsToOfflineRequestCache,
} from '../../services/largeUpsOfflineSync';

import type {
  LargeUpsConfiguration,
  LargeUpsConfigurationsResponse,
  GroupedLargeUpsConfigurations,
} from '../../types/largeUps';

/** @deprecated use isRepeatingCollectionRouteNotFound */
export function isLargeUpsCollectionRouteNotFound(error: unknown): boolean {
  return isRepeatingCollectionRouteNotFound(error);
}

const EMPTY_GROUPED_LARGE_UPS: GroupedLargeUpsConfigurations = {
  existing: [],
  new: [],
};

/**
 * Query keys for consistent cache management
 */
export const largeUpsConfigurationsKeys = {
  all: ['largeUpsConfigurations'] as const,
  lists: () => [...largeUpsConfigurationsKeys.all, 'list'] as const,
  list: (requestId: string) =>
    [...largeUpsConfigurationsKeys.lists(), requestId] as const,
  grouped: (requestId: string) =>
    [...largeUpsConfigurationsKeys.all, 'grouped', requestId] as const,
  details: () => [...largeUpsConfigurationsKeys.all, 'detail'] as const,
  detail: (requestId: string, largeUpsId: number) =>
    [...largeUpsConfigurationsKeys.details(), requestId, largeUpsId] as const,
};

/**
 * Hook for fetching all large UPS configurations for a request
 * Includes offline fallback to return cached configurations
 */
export const useLargeUpsConfigurations = (
  requestId: string,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: largeUpsConfigurationsKeys.list(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflineLargeUpsList(requestId);
      }

      try {
        const response = await api.get<LargeUpsConfigurationsResponse>(
          `/requests/${requestId}/large-ups`,
        );
        await mirrorLargeUpsToOfflineRequestCache(requestId, response.largeUps);
        const pendingOps = await getInFlightLargeUpsOpsForRequest(requestId);
        return applyPendingLargeUpsOperations(
          requestId,
          response.largeUps,
          pendingOps,
        );
      } catch (error) {
        if (isLargeUpsCollectionRouteNotFound(error)) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error) =>
      isLargeUpsCollectionRouteNotFound(error) ? false : failureCount < 3,
    meta: {
      errorMessage: 'Failed to fetch large UPS configurations.',
    },
  });
};

/**
 * Hook for fetching large UPS configurations grouped by status (existing vs new)
 * Includes offline fallback (derived from cached list)
 */
export const useLargeUpsConfigurationsGrouped = (
  requestId: string,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: largeUpsConfigurationsKeys.grouped(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflineLargeUpsGrouped(requestId);
      }

      try {
        const response = await api.get<GroupedLargeUpsConfigurations>(
          `/requests/${requestId}/large-ups/grouped`,
        );
        const allUnits = [...response.existing, ...response.new];
        await mirrorLargeUpsToOfflineRequestCache(requestId, allUnits);
        const pendingOps = await getInFlightLargeUpsOpsForRequest(requestId);
        return groupLargeUpsByStatus(
          applyPendingLargeUpsOperations(requestId, allUnits, pendingOps),
        );
      } catch (error) {
        if (isLargeUpsCollectionRouteNotFound(error)) {
          return EMPTY_GROUPED_LARGE_UPS;
        }
        throw error;
      }
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000,
    retry: (failureCount, error) =>
      isLargeUpsCollectionRouteNotFound(error) ? false : failureCount < 3,
    meta: {
      errorMessage: 'Failed to fetch grouped large UPS configurations.',
    },
  });
};

/**
 * Hook for fetching a single large UPS configuration
 * Includes offline fallback from cached request units
 */
export const useLargeUpsConfiguration = (
  requestId: string,
  largeUpsId: number,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: largeUpsConfigurationsKeys.detail(requestId, largeUpsId),
    queryFn: async () => {
      if (!isOnline) {
        const cached = await getOfflineLargeUpsById(requestId, largeUpsId);
        if (!cached) {
          throw new Error(
            'Cooling system configuration not found in offline cache',
          );
        }
        return cached;
      }

      try {
        const response = await api.get<LargeUpsConfiguration>(
          `/requests/${requestId}/large-ups/${largeUpsId}`,
        );
        const list = await getCachedLargeUpsList(requestId);
        const merged = list.some(u => u.id === response.id)
          ? list.map(u => (u.id === response.id ? response : u))
          : [...list, response];
        await mirrorLargeUpsToOfflineRequestCache(requestId, merged);
        return response;
      } catch (error) {
        if (isLargeUpsCollectionRouteNotFound(error)) {
          throw new Error('Large UPS configuration not found');
        }
        throw error;
      }
    },
    enabled: !!requestId && !!largeUpsId && enabled,
    staleTime: 30 * 1000,
    retry: (failureCount, error) =>
      isLargeUpsCollectionRouteNotFound(error) ? false : failureCount < 3,
    meta: {
      errorMessage: 'Failed to fetch large UPS configuration details.',
    },
  });
};
